<template>
    <div class="my-events-page">
  
      <DefaultHeader
        title="Мои ивенты"
        subtitle="Список ваших ивентов"
        :offset="700"
      />
  
      <main class="my-events-content">
        <div class="my-events-container">
  
          <!-- =====================================================
               HEADING
          ====================================================== -->
  
          <section class="page-heading">
  
            <div>
              <span class="page-label">
                Ивенты
              </span>
  
              <h1>
                Мои ивенты
              </h1>
  
              <p>
                Управляйте своими событиями и следите
                за их статусом публикации.
              </p>
            </div>
  
  
            <button
              type="button"
              class="create-button"
              @click="goToCreateEvent"
            >
              <span class="create-icon">
                +
              </span>
  
              Создать ивент
            </button>
  
          </section>
  
  
          <!-- =====================================================
               SUMMARY
          ====================================================== -->
  
          <section
            v-if="!isLoading && !errorMessage"
            class="summary-row"
          >
  
            <div class="summary-item pending">
              <span class="summary-dot"></span>
  
              <div>
                <strong>
                  {{ pendingEvents.length }}
                </strong>
  
                <span>
                  На модерации
                </span>
              </div>
            </div>
  
  
            <div class="summary-item active">
              <span class="summary-dot"></span>
  
              <div>
                <strong>
                  {{ activeEvents.length }}
                </strong>
  
                <span>
                  Активные
                </span>
              </div>
            </div>
  
  
            <div class="summary-item last">
              <span class="summary-dot"></span>
  
              <div>
                <strong>
                  {{ lastEvents.length }}
                </strong>
  
                <span>
                  Завершённые
                </span>
              </div>
            </div>
  
          </section>
  
  
          <!-- =====================================================
               LOADING
          ====================================================== -->
  
          <template v-if="isLoading">
  
            <section class="events-section">
  
              <div class="section-heading">
                <div>
                  <div class="skeleton-line section-title-skeleton"></div>
                  <div class="skeleton-line section-subtitle-skeleton"></div>
                </div>
              </div>
  
              <div class="events-grid">
  
                <div
                  v-for="index in 4"
                  :key="index"
                  class="event-card skeleton-card"
                >
                  <div class="skeleton-image"></div>
  
                  <div class="skeleton-content">
                    <div class="skeleton-line small"></div>
                    <div class="skeleton-line title"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line short"></div>
                  </div>
                </div>
  
              </div>
  
            </section>
  
          </template>
  
  
          <!-- =====================================================
               ERROR
          ====================================================== -->
  
          <section
            v-else-if="errorMessage"
            class="state-card"
          >
  
            <div class="state-icon error">
              !
            </div>
  
            <h2>
              Не удалось загрузить ивенты
            </h2>
  
            <p>
              {{ errorMessage }}
            </p>
  
            <button
              type="button"
              class="retry-button"
              @click="loadEvents"
            >
              Попробовать снова
            </button>
  
          </section>
  
  
          <!-- =====================================================
               EMPTY
          ====================================================== -->
  
          <section
            v-else-if="allEventsCount === 0"
            class="state-card empty"
          >
  
            <div class="state-icon calendar">
  
              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <rect
                  x="4"
                  y="5"
                  width="16"
                  height="15"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
  
                <path
                  d="M8 3V7"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
  
                <path
                  d="M16 3V7"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
  
                <path
                  d="M4 9H20"
                  stroke="currentColor"
                  stroke-width="1.7"
                />
              </svg>
  
            </div>
  
            <h2>
              У вас пока нет ивентов
            </h2>
  
            <p>
              Создайте первое событие — после отправки
              оно появится здесь со статусом модерации.
            </p>
  
            <button
              type="button"
              class="create-empty-button"
              @click="goToCreateEvent"
            >
              Создать ивент
            </button>
  
          </section>
  
  
          <!-- =====================================================
               CONTENT
          ====================================================== -->
  
          <template v-else>
  
            <!-- PENDING -->
  
            <section
              v-if="pendingEvents.length"
              class="events-section"
            >
  
              <div class="section-heading">
  
                <div>
                  <div class="section-title-row">
  
                    <span class="section-indicator pending"></span>
  
                    <h2>
                      На модерации
                    </h2>
  
                    <span class="section-count">
                      {{ pendingEvents.length }}
                    </span>
  
                  </div>
  
                  <p>
                    События, которые ожидают публикации
                  </p>
                </div>
  
              </div>
  
  
              <div class="events-grid">
  
                <article
                  v-for="event in pendingEvents"
                  :key="`pending-${event.id}`"
                  class="event-card pending-card"
                >
  
                  <div class="event-image-wrapper">
  
                    <img
                      v-if="event.imageURL"
                      :src="event.imageURL"
                      :alt="event.title || 'Ивент'"
                      class="event-image"
                    />
  
                    <div
                      v-else
                      class="event-image-placeholder"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="4"
                          y="5"
                          width="16"
                          height="15"
                          rx="3"
                          stroke="currentColor"
                          stroke-width="1.7"
                        />
  
                        <path
                          d="M8 3V7"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                        />
  
                        <path
                          d="M16 3V7"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                        />
  
                        <path
                          d="M4 9H20"
                          stroke="currentColor"
                          stroke-width="1.7"
                        />
                      </svg>
                    </div>
  
  
                    <div class="status-badge pending">
                      <span></span>
  
                      {{
                        event.moderation_type === "edit"
                          ? "Изменения на модерации"
                          : "На модерации"
                      }}
                    </div>
  
  
                    <div
                      v-if="event.date_of_event"
                      class="date-badge"
                    >
                      <strong>
                        {{ eventDay(event.date_of_event) }}
                      </strong>
  
                      <span>
                        {{ eventMonth(event.date_of_event) }}
                      </span>
                    </div>
  
                  </div>
  
  
                  <div class="event-body">
  
                    <span class="event-type">
                      {{
                        event.moderation_type === "edit"
                          ? "Редактирование"
                          : "Новый ивент"
                      }}
                    </span>
  
  
                    <h3>
                      {{ event.title || "Без названия" }}
                    </h3>
  
  
                    <p
                      v-if="event.preview_title"
                      class="event-preview"
                    >
                      {{ event.preview_title }}
                    </p>
  
  
                    <div class="event-meta">
  
                      <div class="meta-item">
  
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <rect
                            x="5"
                            y="5"
                            width="14"
                            height="14"
                            rx="3"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
  
                          <path
                            d="M8 3V7M16 3V7M5 9H19"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                          />
                        </svg>
  
                        {{ formatEventDate(event.date_of_event) }}
  
                      </div>
  
  
                      <div
                        v-if="event.address_title"
                        class="meta-item"
                      >
  
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
  
                          <circle
                            cx="12"
                            cy="10"
                            r="2"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
                        </svg>
  
                        {{ event.address_title }}
  
                      </div>
  
                    </div>
  
  
                    <p class="event-description">
                      {{
                        event.description
                        || "Описание события отсутствует."
                      }}
                    </p>
  
  
                    <div
                      v-if="event.moderation_type === 'edit'"
                      class="moderation-info"
                    >
                      <span class="moderation-info-icon">
                        ✎
                      </span>
  
                      <span>
                        Опубликованная версия остаётся доступной,
                        пока изменения проходят модерацию.
                      </span>
                    </div>
  
                  </div>
  
                </article>
  
              </div>
  
            </section>
  
  
            <!-- ACTIVE -->
  
            <section
              v-if="activeEvents.length"
              class="events-section"
            >
  
              <div class="section-heading">
  
                <div>
                  <div class="section-title-row">
  
                    <span class="section-indicator active"></span>
  
                    <h2>
                      Активные
                    </h2>
  
                    <span class="section-count">
                      {{ activeEvents.length }}
                    </span>
  
                  </div>
  
                  <p>
                    Опубликованные события
                  </p>
                </div>
  
              </div>
  
  
              <div class="events-grid">
  
                <article
                  v-for="event in activeEvents"
                  :key="`active-${event.id}`"
                  class="event-card active-card"
                >
  
                  <div class="event-image-wrapper">
  
                    <img
                      v-if="event.image_url"
                      :src="event.image_url"
                      :alt="event.title || 'Ивент'"
                      class="event-image"
                    />
  
                    <div
                      v-else
                      class="event-image-placeholder"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="4"
                          y="5"
                          width="16"
                          height="15"
                          rx="3"
                          stroke="currentColor"
                          stroke-width="1.7"
                        />
  
                        <path
                          d="M8 3V7M16 3V7M4 9H20"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>
  
  
                    <div class="status-badge active">
                      <span></span>
                      Опубликован
                    </div>
  
  
                    <div
                      v-if="event.date_of_event"
                      class="date-badge"
                    >
                      <strong>
                        {{ eventDay(event.date_of_event) }}
                      </strong>
  
                      <span>
                        {{ eventMonth(event.date_of_event) }}
                      </span>
                    </div>
  
                  </div>
  
  
                  <div class="event-body">
  
                    <span class="event-type">
                      Ивент
                    </span>
  
                    <h3>
                      {{ event.title || "Без названия" }}
                    </h3>
  
  
                    <p
                      v-if="event.preview_title"
                      class="event-preview"
                    >
                      {{ event.preview_title }}
                    </p>
  
  
                    <div class="event-meta">
  
                      <div class="meta-item">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <rect
                            x="5"
                            y="5"
                            width="14"
                            height="14"
                            rx="3"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
  
                          <path
                            d="M8 3V7M16 3V7M5 9H19"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                          />
                        </svg>
  
                        {{ formatEventDate(event.date_of_event) }}
                      </div>
  
  
                      <div
                        v-if="event.address_title"
                        class="meta-item"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
  
                          <circle
                            cx="12"
                            cy="10"
                            r="2"
                            stroke="currentColor"
                            stroke-width="1.6"
                          />
                        </svg>
  
                        {{ event.address_title }}
                      </div>
  
                    </div>
  
  
                    <p class="event-description">
                      {{
                        event.description
                        || "Описание события отсутствует."
                      }}
                    </p>
  
  
                    <div class="event-footer">
  
                      <a
                        v-if="event.link_url"
                        :href="event.link_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="event-link"
                      >
                        Открыть событие
                      </a>
  
  
                      <span
                        v-else
                        class="event-id"
                      >
                        {{ event.id }}
                      </span>
  
                    </div>
  
                  </div>
  
                </article>
  
              </div>
  
            </section>
  
  
            <!-- LAST -->
  
            <section
              v-if="lastEvents.length"
              class="events-section"
            >
  
              <div class="section-heading">
  
                <div>
                  <div class="section-title-row">
  
                    <span class="section-indicator last"></span>
  
                    <h2>
                      Завершённые
                    </h2>
  
                    <span class="section-count">
                      {{ lastEvents.length }}
                    </span>
  
                  </div>
  
                  <p>
                    Прошедшие события
                  </p>
                </div>
  
              </div>
  
  
              <div class="events-grid">
  
                <article
                  v-for="event in lastEvents"
                  :key="`last-${event.id}`"
                  class="event-card last-card"
                >
  
                  <div class="event-image-wrapper">
  
                    <img
                      v-if="event.image_url"
                      :src="event.image_url"
                      :alt="event.title || 'Ивент'"
                      class="event-image"
                    />
  
                    <div
                      v-else
                      class="event-image-placeholder"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="4"
                          y="5"
                          width="16"
                          height="15"
                          rx="3"
                          stroke="currentColor"
                          stroke-width="1.7"
                        />
  
                        <path
                          d="M8 3V7M16 3V7M4 9H20"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>
  
  
                    <div class="status-badge last">
                      Завершён
                    </div>
  
  
                    <div
                      v-if="event.date_of_event"
                      class="date-badge"
                    >
                      <strong>
                        {{ eventDay(event.date_of_event) }}
                      </strong>
  
                      <span>
                        {{ eventMonth(event.date_of_event) }}
                      </span>
                    </div>
  
                  </div>
  
  
                  <div class="event-body">
  
                    <span class="event-type">
                      Архив
                    </span>
  
                    <h3>
                      {{ event.title || "Без названия" }}
                    </h3>
  
                    <p class="event-description">
                      {{
                        event.description
                        || "Описание события отсутствует."
                      }}
                    </p>
  
                    <div class="event-footer">
                      <span class="event-id">
                        {{ event.id }}
                      </span>
                    </div>
  
                  </div>
  
                </article>
  
              </div>
  
            </section>
  
          </template>
  
        </div>
      </main>
  
  
      <Footer />
  
    </div>
  </template>
  
  
  <script setup>
  
  import {
    computed,
    onMounted,
    ref
  } from "vue";
  
  import {
    useRouter
  } from "vue-router";
  
  import DefaultHeader
    from "../components/MyEventsHeader.vue";
  
  import Footer
    from "../components/MiniFooter.vue";
  
  import EventService
    from "../api/EventService";
  
  
  const router =
    useRouter();
  
  
  const pendingEvents =
    ref([]);
  
  const activeEvents =
    ref([]);
  
  const lastEvents =
    ref([]);
  
  
  const isLoading =
    ref(true);
  
  const errorMessage =
    ref("");
  
  
  const allEventsCount =
    computed(() => {
  
      return (
        pendingEvents.value.length
        + activeEvents.value.length
        + lastEvents.value.length
      );
  
    });
  
  
  onMounted(() => {
    loadEvents();
  });
  
  
  async function loadEvents() {
  
    isLoading.value = true;
    errorMessage.value = "";
  
    try {
  
      const result =
        await EventService.fetchMyEvents();
  
      console.log(
        "📅 MyEvents result:",
        result
      );
  
  
      /*
       * Если твой EventService.fetchMyEvents()
       * сейчас возвращает объект:
       *
       * {
       *   pendingEvents,
       *   activeEvents,
       *   lastEvents
       * }
       */
  
      pendingEvents.value =
        result?.pendingEvents
        ?? [];
  
      activeEvents.value =
        result?.activeEvents
        ?? [];
  
      lastEvents.value =
        result?.lastEvents
        ?? [];
  
  
    } catch (error) {
  
      console.error(
        "❌ Ошибка загрузки моих ивентов:",
        error.response?.data
        || error.message
        || error
      );
  
      errorMessage.value =
        error.response?.data?.error
        || error.message
        || "Произошла неизвестная ошибка";
  
    } finally {
  
      isLoading.value = false;
  
    }
  
  }
  
  
  function goToCreateEvent() {
  
    router.push(
      "/create-event"
    );
  
  }
  
  
  /* ============================================================
     DATE
  ============================================================ */
  
  function timestampToDate(value) {
  
    if (
      value === null
      || value === undefined
      || value === ""
    ) {
      return null;
    }
  
  
    const number =
      Number(value);
  
  
    if (
      !Number.isNaN(number)
    ) {
  
      const milliseconds =
        number < 100000000000
          ? number * 1000
          : number;
  
      const result =
        new Date(milliseconds);
  
      if (
        !Number.isNaN(
          result.getTime()
        )
      ) {
        return result;
      }
  
    }
  
  
    const result =
      new Date(value);
  
  
    if (
      Number.isNaN(
        result.getTime()
      )
    ) {
      return null;
    }
  
  
    return result;
  
  }
  
  
  function formatEventDate(value) {
  
    const date =
      timestampToDate(value);
  
  
    if (!date) {
      return "Дата не указана";
    }
  
  
    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "numeric",
        month: "long",
        year: "numeric"
      }
    ).format(date);
  
  }
  
  
  function eventDay(value) {
  
    const date =
      timestampToDate(value);
  
  
    if (!date) {
      return "—";
    }
  
  
    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        day: "2-digit"
      }
    ).format(date);
  
  }
  
  
  function eventMonth(value) {
  
    const date =
      timestampToDate(value);
  
  
    if (!date) {
      return "";
    }
  
  
    return new Intl.DateTimeFormat(
      "ru-RU",
      {
        month: "short"
      }
    )
      .format(date)
      .replace(".", "")
      .toUpperCase();
  
  }
  
  </script>
  
  
  <style scoped>
  
  * {
    box-sizing: border-box;
  }
  
  
  /* ============================================================
     PAGE
  ============================================================ */
  
  .my-events-page {
    min-height: 100dvh;
  
    display: flex;
    flex-direction: column;
  
    color: #24272d;
  
    background: #f4f6f9;
  }
  
  
  .my-events-content {
    flex: 1;
  
    padding:
      50px
      24px
      80px;
  
    background:
      radial-gradient(
        circle at 15% 0%,
        rgba(46, 132, 246, 0.08),
        transparent 360px
      ),
      radial-gradient(
        circle at 88% 8%,
        rgba(106, 88, 223, 0.055),
        transparent 330px
      ),
      #f4f6f9;
  }
  
  
  .my-events-container {
    width: 100%;
    max-width: 1180px;
  
    margin: 0 auto;
  }
  
  
  /* ============================================================
     HEADING
  ============================================================ */
  
  .page-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  
    gap: 30px;
  
    margin-bottom: 26px;
  }
  
  
  .page-label {
    display: block;
  
    margin-bottom: 6px;
  
    color: #969da8;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    letter-spacing: 0.09em;
  
    text-transform: uppercase;
  }
  
  
  .page-heading h1 {
    margin: 0;
  
    color: #22262d;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 30px;
    line-height: 1.2;
  }
  
  
  .page-heading p {
    max-width: 520px;
  
    margin:
      9px
      0
      0;
  
    color: #7c838e;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 13px;
  
    line-height: 1.55;
  }
  
  
  /* ============================================================
     CREATE
  ============================================================ */
  
  .create-button,
  .create-empty-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  
    gap: 8px;
  
    min-height: 42px;
  
    padding:
      0
      17px;
  
    color: #ffffff;
  
    background:
      linear-gradient(
        135deg,
        #2887f7,
        #486de6
      );
  
    border: 0;
    border-radius: 12px;
  
    box-shadow:
      0 8px 20px
      rgba(
        40,
        135,
        247,
        0.2
      );
  
    cursor: pointer;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  
    transition:
      transform
      0.2s ease,
      box-shadow
      0.2s ease;
  }
  
  
  .create-button:hover,
  .create-empty-button:hover {
    transform:
      translateY(-1px);
  
    box-shadow:
      0 11px 25px
      rgba(
        40,
        135,
        247,
        0.27
      );
  }
  
  
  .create-icon {
    font-size: 18px;
    line-height: 1;
  }
  
  
  /* ============================================================
     SUMMARY
  ============================================================ */
  
  .summary-row {
    display: flex;
    align-items: center;
  
    gap: 10px;
  
    margin-bottom: 34px;
  
    padding-bottom: 22px;
  
    border-bottom:
      1px solid
      rgba(
        31,
        44,
        62,
        0.06
      );
  }
  
  
  .summary-item {
    display: flex;
    align-items: center;
  
    gap: 10px;
  
    min-width: 140px;
  
    padding:
      10px
      13px;
  
    background:
      rgba(
        255,
        255,
        255,
        0.68
      );
  
    border:
      1px solid
      rgba(
        31,
        44,
        62,
        0.055
      );
  
    border-radius: 13px;
  }
  
  
  .summary-dot {
    width: 8px;
    height: 8px;
  
    flex-shrink: 0;
  
    border-radius: 50%;
  }
  
  
  .summary-item.pending
  .summary-dot {
    background: #ed9c38;
  
    box-shadow:
      0 0 0 4px
      rgba(
        237,
        156,
        56,
        0.1
      );
  }
  
  
  .summary-item.active
  .summary-dot {
    background: #37ad72;
  
    box-shadow:
      0 0 0 4px
      rgba(
        55,
        173,
        114,
        0.1
      );
  }
  
  
  .summary-item.last
  .summary-dot {
    background: #9299a4;
  
    box-shadow:
      0 0 0 4px
      rgba(
        146,
        153,
        164,
        0.1
      );
  }
  
  
  .summary-item div {
    display: flex;
    align-items: baseline;
  
    gap: 5px;
  }
  
  
  .summary-item strong {
    color: #333841;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 14px;
  }
  
  
  .summary-item span:not(.summary-dot) {
    color: #8b929d;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  }
  
  
  /* ============================================================
     SECTION
  ============================================================ */
  
  .events-section {
    margin-bottom: 46px;
  }
  
  
  .section-heading {
    margin-bottom: 17px;
  }
  
  
  .section-title-row {
    display: flex;
    align-items: center;
  
    gap: 8px;
  }
  
  
  .section-indicator {
    width: 8px;
    height: 8px;
  
    flex-shrink: 0;
  
    border-radius: 50%;
  }
  
  
  .section-indicator.pending {
    background: #ed9c38;
  }
  
  
  .section-indicator.active {
    background: #39ae72;
  }
  
  
  .section-indicator.last {
    background: #8f96a0;
  }
  
  
  .section-heading h2 {
    margin: 0;
  
    color: #292e35;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 18px;
  }
  
  
  .section-heading p {
    margin:
      4px
      0
      0
      16px;
  
    color: #969da7;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 10px;
  }
  
  
  .section-count {
    min-width: 23px;
  
    padding:
      3px
      7px;
  
    color: #858c97;
  
    background:
      rgba(
        120,
        130,
        145,
        0.09
      );
  
    border-radius: 999px;
  
    text-align: center;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 9px;
  }
  
  
  /* ============================================================
     GRID
  ============================================================ */
  
  .events-grid {
    display: grid;
  
    grid-template-columns:
      repeat(
        2,
        minmax(
          0,
          1fr
        )
      );
  
    gap: 20px;
  }
  
  
  /* ============================================================
     CARD
  ============================================================ */
  
  .event-card {
    min-width: 0;
  
    overflow: hidden;
  
    background: #ffffff;
  
    border:
      1px solid
      rgba(
        30,
        43,
        61,
        0.07
      );
  
    border-radius: 22px;
  
    box-shadow:
      0 10px 30px
      rgba(
        15,
        23,
        42,
        0.045
      );
  
    transition:
      transform
      0.22s ease,
      box-shadow
      0.22s ease,
      border-color
      0.22s ease;
  }
  
  
  .event-card:hover {
    transform:
      translateY(-3px);
  
    border-color:
      rgba(
        53,
        132,
        247,
        0.15
      );
  
    box-shadow:
      0 18px 42px
      rgba(
        40,
        52,
        80,
        0.1
      );
  }
  
  
  .last-card {
    opacity: 0.78;
  }
  
  
  /* ============================================================
     IMAGE
  ============================================================ */
  
  .event-image-wrapper {
    position: relative;
  
    height: 220px;
  
    overflow: hidden;
  
    background: #eef1f5;
  }
  
  
  .event-image {
    width: 100%;
    height: 100%;
  
    display: block;
  
    object-fit: cover;
  
    transition:
      transform
      0.35s ease;
  }
  
  
  .event-card:hover
  .event-image {
    transform:
      scale(1.015);
  }
  
  
  .event-image-placeholder {
    width: 100%;
    height: 100%;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #afb5bf;
  
    background:
      linear-gradient(
        135deg,
        #eef1f5,
        #e6eaf0
      );
  }
  
  
  .event-image-placeholder svg {
    width: 44px;
    height: 44px;
  }
  
  
  /* ============================================================
     STATUS
  ============================================================ */
  
  .status-badge {
    position: absolute;
  
    top: 15px;
    left: 15px;
  
    display: inline-flex;
    align-items: center;
  
    gap: 6px;
  
    padding:
      7px
      10px;
  
    color: white;
  
    border-radius: 10px;
  
    border:
      1px solid
      rgba(
        255,
        255,
        255,
        0.14
      );
  
    backdrop-filter:
      blur(12px);
  
    -webkit-backdrop-filter:
      blur(12px);
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 9px;
  }
  
  
  .status-badge span {
    width: 5px;
    height: 5px;
  
    border-radius: 50%;
  
    background: currentColor;
  }
  
  
  .status-badge.pending {
    background:
      rgba(
        208,
        133,
        39,
        0.9
      );
  }
  
  
  .status-badge.active {
    background:
      rgba(
        39,
        154,
        93,
        0.9
      );
  }
  
  
  .status-badge.last {
    background:
      rgba(
        63,
        68,
        77,
        0.82
      );
  }
  
  
  /* ============================================================
     DATE BADGE
  ============================================================ */
  
  .date-badge {
    position: absolute;
  
    top: 15px;
    right: 15px;
  
    width: 52px;
    height: 52px;
  
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  
    color: #252930;
  
    background:
      rgba(
        255,
        255,
        255,
        0.93
      );
  
    border-radius: 14px;
  
    box-shadow:
      0 8px 18px
      rgba(
        0,
        0,
        0,
        0.12
      );
  
    backdrop-filter:
      blur(10px);
  }
  
  
  .date-badge strong {
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 18px;
    line-height: 1;
  }
  
  
  .date-badge span {
    margin-top: 3px;
  
    color: #777e89;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 8px;
  
    letter-spacing: 0.04em;
  }
  
  
  /* ============================================================
     BODY
  ============================================================ */
  
  .event-body {
    padding:
      20px
      20px
      18px;
  }
  
  
  .event-type {
    display: block;
  
    margin-bottom: 5px;
  
    color: #9299a3;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 8px;
  
    letter-spacing: 0.07em;
  
    text-transform: uppercase;
  }
  
  
  .event-body h3 {
    margin: 0;
  
    color: #272b32;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 19px;
  
    line-height: 1.3;
  }
  
  
  .event-preview {
    margin:
      6px
      0
      0;
  
    color: #7e8590;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    line-height: 1.45;
  }
  
  
  /* ============================================================
     META
  ============================================================ */
  
  .event-meta {
    display: flex;
    flex-wrap: wrap;
  
    gap:
      8px
      15px;
  
    margin-top: 13px;
  }
  
  
  .meta-item {
    min-width: 0;
  
    display: flex;
    align-items: center;
  
    gap: 5px;
  
    color: #7e8691;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 10px;
  }
  
  
  .meta-item svg {
    width: 14px;
    height: 14px;
  
    flex-shrink: 0;
  }
  
  
  /* ============================================================
     DESCRIPTION
  ============================================================ */
  
  .event-description {
    display: -webkit-box;
  
    margin:
      15px
      0
      0;
  
    overflow: hidden;
  
    color: #676f7a;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 11px;
  
    line-height: 1.6;
  
    -webkit-box-orient:
      vertical;
  
    -webkit-line-clamp:
      4;
  }
  
  
  /* ============================================================
     MODERATION INFO
  ============================================================ */
  
  .moderation-info {
    display: flex;
    align-items: flex-start;
  
    gap: 9px;
  
    margin-top: 15px;
  
    padding:
      11px
      12px;
  
    color: #7d6bcd;
  
    background:
      rgba(
        124,
        92,
        231,
        0.065
      );
  
    border:
      1px solid
      rgba(
        124,
        92,
        231,
        0.11
      );
  
    border-radius: 12px;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  
    line-height: 1.45;
  }
  
  
  .moderation-info-icon {
    width: 24px;
    height: 24px;
  
    flex-shrink: 0;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #ffffff;
  
    background: #7964d8;
  
    border-radius: 7px;
  }
  
  
  /* ============================================================
     FOOTER
  ============================================================ */
  
  .event-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  
    margin-top: 17px;
  
    padding-top: 15px;
  
    border-top:
      1px solid
      rgba(
        31,
        44,
        62,
        0.06
      );
  }
  
  
  .event-link {
    color: #397ce0;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    text-decoration: none;
  }
  
  
  .event-link:hover {
    text-decoration: underline;
  }
  
  
  .event-id {
    max-width: 100%;
  
    overflow: hidden;
  
    color: #a0a6af;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 8px;
  
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  
  /* ============================================================
     STATE
  ============================================================ */
  
  .state-card {
    max-width: 520px;
  
    margin:
      75px
      auto;
  
    padding:
      42px
      34px;
  
    text-align: center;
  
    background: #ffffff;
  
    border:
      1px solid
      rgba(
        31,
        44,
        62,
        0.07
      );
  
    border-radius: 22px;
  
    box-shadow:
      0 14px 38px
      rgba(
        15,
        23,
        42,
        0.055
      );
  }
  
  
  .state-icon {
    width: 54px;
    height: 54px;
  
    margin:
      0
      auto
      17px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: white;
  
    border-radius: 16px;
  }
  
  
  .state-icon.error {
    background:
      linear-gradient(
        135deg,
        #e76b68,
        #ca4d54
      );
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 20px;
  }
  
  
  .state-icon.calendar {
    background:
      linear-gradient(
        135deg,
        #2887f7,
        #6659df
      );
  }
  
  
  .state-icon.calendar svg {
    width: 25px;
    height: 25px;
  }
  
  
  .state-card h2 {
    margin: 0;
  
    color: #282c33;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 18px;
  }
  
  
  .state-card p {
    max-width: 390px;
  
    margin:
      9px
      auto
      0;
  
    color: #848b95;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 12px;
  
    line-height: 1.55;
  }
  
  
  .retry-button {
    margin-top: 20px;
  
    padding:
      11px
      16px;
  
    color: white;
  
    background: #347fe7;
  
    border: 0;
  
    border-radius: 11px;
  
    cursor: pointer;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 11px;
  }
  
  
  .create-empty-button {
    margin-top: 22px;
  }
  
  
  /* ============================================================
     SKELETON
  ============================================================ */
  
  .skeleton-card {
    pointer-events: none;
  }
  
  
  .skeleton-image,
  .skeleton-line {
    background:
      linear-gradient(
        90deg,
        #edf0f3 25%,
        #f6f7f9 38%,
        #edf0f3 63%
      );
  
    background-size:
      400% 100%;
  
    animation:
      skeleton-shimmer
      1.4s
      ease
      infinite;
  }
  
  
  .skeleton-image {
    height: 220px;
  }
  
  
  .skeleton-content {
    padding: 21px;
  }
  
  
  .skeleton-line {
    height: 11px;
  
    margin-bottom: 10px;
  
    border-radius: 6px;
  }
  
  
  .skeleton-line.small {
    width: 28%;
    height: 8px;
  }
  
  
  .skeleton-line.title {
    width: 72%;
    height: 20px;
  }
  
  
  .skeleton-line.short {
    width: 55%;
  }
  
  
  .section-title-skeleton {
    width: 160px;
    height: 18px;
  }
  
  
  .section-subtitle-skeleton {
    width: 230px;
    height: 9px;
  }
  
  
  @keyframes skeleton-shimmer {
  
    0% {
      background-position:
        100% 0;
    }
  
    100% {
      background-position:
        -100% 0;
    }
  
  }
  
  
  /* ============================================================
     DARK
  ============================================================ */
  
  html[data-theme="dark"]
  .my-events-page {
    color: #f2f2f4;
  
    background: #18191c;
  }
  
  
  html[data-theme="dark"]
  .my-events-content {
    background:
      radial-gradient(
        circle at 15% 0%,
        rgba(53, 132, 247, 0.07),
        transparent 360px
      ),
      radial-gradient(
        circle at 88% 8%,
        rgba(106, 88, 223, 0.06),
        transparent 330px
      ),
      #18191c;
  }
  
  
  html[data-theme="dark"]
  .page-heading h1,
  html[data-theme="dark"]
  .section-heading h2,
  html[data-theme="dark"]
  .event-body h3,
  html[data-theme="dark"]
  .state-card h2 {
    color: #f1f2f4;
  }
  
  
  html[data-theme="dark"]
  .page-heading p {
    color: #939aa5;
  }
  
  
  html[data-theme="dark"]
  .page-label {
    color: #747b85;
  }
  
  
  html[data-theme="dark"]
  .summary-row {
    border-bottom-color:
      rgba(
        255,
        255,
        255,
        0.06
      );
  }
  
  
  html[data-theme="dark"]
  .summary-item {
    background: #232428;
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.06
      );
  }
  
  
  html[data-theme="dark"]
  .summary-item strong {
    color: #f0f1f3;
  }
  
  
  html[data-theme="dark"]
  .event-card,
  html[data-theme="dark"]
  .state-card {
    background: #232428;
  
    border-color:
      rgba(
        255,
        255,
        255,
        0.065
      );
  
    box-shadow:
      0 12px 30px
      rgba(
        0,
        0,
        0,
        0.2
      );
  }
  
  
  html[data-theme="dark"]
  .event-card:hover {
    border-color:
      rgba(
        73,
        132,
        224,
        0.17
      );
  
    box-shadow:
      0 18px 44px
      rgba(
        0,
        0,
        0,
        0.32
      );
  }
  
  
  html[data-theme="dark"]
  .event-image-wrapper {
    background: #2d2f34;
  }
  
  
  html[data-theme="dark"]
  .event-image-placeholder {
    color: #676e78;
  
    background:
      linear-gradient(
        135deg,
        #292b30,
        #33363c
      );
  }
  
  
  html[data-theme="dark"]
  .event-preview,
  html[data-theme="dark"]
  .event-description,
  html[data-theme="dark"]
  .meta-item {
    color: #9fa5ae;
  }
  
  
  html[data-theme="dark"]
  .event-footer {
    border-top-color:
      rgba(
        255,
        255,
        255,
        0.07
      );
  }
  
  
  html[data-theme="dark"]
  .moderation-info {
    color: #b6a9ed;
  
    background:
      rgba(
        124,
        92,
        231,
        0.09
      );
  
    border-color:
      rgba(
        124,
        92,
        231,
        0.14
      );
  }
  
  
  html[data-theme="dark"]
  .skeleton-image,
  html[data-theme="dark"]
  .skeleton-line {
    background:
      linear-gradient(
        90deg,
        #292b30 25%,
        #34363c 38%,
        #292b30 63%
      );
  
    background-size:
      400% 100%;
  }
  
  
  /* ============================================================
     TABLET
  ============================================================ */
  
  @media (
    max-width: 900px
  ) {
  
    .my-events-content {
      padding:
        38px
        18px
        70px;
    }
  
  
    .events-grid {
      grid-template-columns:
        1fr;
    }
  
  }
  
  
  /* ============================================================
     MOBILE
  ============================================================ */
  
  @media (
    max-width: 600px
  ) {
  
    .my-events-content {
      padding:
        28px
        14px
        60px;
    }
  
  
    .page-heading {
      flex-direction: column;
  
      gap: 18px;
  
      margin-bottom: 22px;
    }
  
  
    .page-heading h1 {
      font-size: 25px;
    }
  
  
    .page-heading p {
      font-size: 12px;
    }
  
  
    .create-button {
      width: 100%;
    }
  
  
    .summary-row {
      width: 100%;
  
      overflow-x: auto;
  
      gap: 8px;
  
      padding-bottom: 17px;
    }
  
  
    .summary-item {
      min-width: 125px;
    }
  
  
    .event-image-wrapper,
    .skeleton-image {
      height: 190px;
    }
  
  
    .event-card {
      border-radius: 18px;
    }
  
  
    .event-body {
      padding:
        17px
        16px
        16px;
    }
  
  
    .event-body h3 {
      font-size: 17px;
    }
  
  
    .events-section {
      margin-bottom: 38px;
    }
  
  }
  
  
  /* ============================================================
     REDUCED MOTION
  ============================================================ */
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
  
    .event-card,
    .create-button,
    .create-empty-button {
      transition: none;
    }
  
  
    .skeleton-image,
    .skeleton-line {
      animation: none;
    }
  
  }
  
  </style>